import { useCallback, useEffect, useRef, useState } from 'react';
import { getToken } from '../services/api';

// Live data logger updates from /api/ws (logger_stream.py): status changes for
// every logger, plus the readings of one subscribed logger for the Data Stream
// tab. One socket per page; it reconnects with backoff, and when the server is
// full ('busy') or unreachable the page falls back to polling.

// How much of the stream is kept for the graph, measured back from the newest
// record (logger clock).
const STREAM_WINDOW_MS = 30 * 60 * 1000;
const RETRY_MS = [2000, 5000, 15000, 30000, 60000];
const BUSY_RETRY_MS = 60000;

const socketUrl = () =>
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/ws`;

const rowKey = (r) => `${r.Timestamp}|${r.RecordNo}`;

function mergeRows(existing, incoming) {
  const byKey = new Map(existing.map((r) => [rowKey(r), r]));
  incoming.forEach((r) => byKey.set(rowKey(r), r));
  const rows = [...byKey.values()].sort(
    (a, b) => a.Timestamp.localeCompare(b.Timestamp) || a.RecordNo - b.RecordNo,
  );
  if (!rows.length) return rows;
  const newest = Date.parse(rows[rows.length - 1].Timestamp);
  return rows.filter((r) => newest - Date.parse(r.Timestamp) <= STREAM_WINDOW_MS);
}

/**
 * @param onStatus called with [{id, status, last_post_at}] whenever loggers'
 *                 statuses change (the first call carries every logger)
 * @returns connection: 'connecting' | 'live' | 'unavailable'
 *          stream: { loggerId, rows, loaded } for the subscribed logger
 *          setStreamLoggerId: subscribe to a logger (null to stop)
 */
export default function useLoggerSocket(onStatus) {
  const [connection, setConnection] = useState('connecting');
  const [streamLoggerId, setStreamLoggerIdState] = useState(null);
  const [stream, setStream] = useState({ loggerId: null, rows: [], loaded: false });

  const wsRef = useRef(null);
  const readyRef = useRef(false);
  const onStatusRef = useRef(onStatus);
  const streamIdRef = useRef(null);
  onStatusRef.current = onStatus;

  useEffect(() => {
    let closed = false;
    let attempt = 0;
    let retryTimer = null;

    const connect = () => {
      const token = getToken();
      if (!token || closed) return;
      setConnection('connecting');
      const ws = new WebSocket(socketUrl());
      wsRef.current = ws;
      readyRef.current = false;
      let busy = false;

      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        if (msg.type === 'ready') {
          attempt = 0;
          readyRef.current = true;
          setConnection('live');
          if (streamIdRef.current != null) {
            ws.send(JSON.stringify({ type: 'subscribe', logger_id: streamIdRef.current }));
          }
        } else if (msg.type === 'status') {
          onStatusRef.current?.(msg.loggers || []);
        } else if (msg.type === 'readings') {
          if (msg.logger_id !== streamIdRef.current) return;
          setStream((prev) => ({
            loggerId: msg.logger_id,
            loaded: true,
            rows: msg.initial || prev.loggerId !== msg.logger_id
              ? mergeRows([], msg.rows || [])
              : mergeRows(prev.rows, msg.rows || []),
          }));
        } else if (msg.type === 'busy') {
          busy = true;
        }
      };
      ws.onclose = () => {
        readyRef.current = false;
        if (closed) return;
        setConnection('unavailable');
        const delay = busy ? BUSY_RETRY_MS : RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retryTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const setStreamLoggerId = useCallback((loggerId) => {
    const id = loggerId ?? null;
    if (id === streamIdRef.current) return;
    streamIdRef.current = id;
    setStreamLoggerIdState(id);
    setStream({ loggerId: id, rows: [], loaded: false });
    // Not connected yet: the 'ready' handler subscribes from streamIdRef.
    const ws = wsRef.current;
    if (ws && readyRef.current && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(id == null ? { type: 'unsubscribe' } : { type: 'subscribe', logger_id: id }));
    }
  }, []);

  return { connection, stream, streamLoggerId, setStreamLoggerId };
}
